import { RecommendHit } from "@algolia/recommend";
import { useQuery } from "@tanstack/react-query";
import { Entry } from "contentful";
import { useRouter } from "next/router";
import { useMemo } from "react";

import SkeletonCarousel from "~/components/common/skeletons/skeleton-carousel";
import { useConfiguration } from "~/contexts/configuration";
import { useServerSideProps } from "~/contexts/server-side-props";
import { useCartWithPendingStatus } from "~/hooks/use-cart";
import {
  cartRecommenderElegibleProduct,
  mergeHistWithProductsInformation,
  recommenderRequest,
} from "~/services/algolia-utils";
import Editorials from "~/types/editorials";
import appRoutes from "~/utils/app-routes";
import evaUtils from "~/utils/eva-utils";

import { CarouselWrapper } from "../carousel-utils/carousel-utils";
import ProductCarouselSwiper from "./product-carousel-swiper";

type Props = {
  entry: Entry<Editorials.ProductCarousel>;
  productReferenceFields: Editorials.ProductsAlgoliaRecommender;
  enableLoop: boolean;
  listScope: string;
};

export default function ProductCarouselAlgolia({ entry, productReferenceFields, enableLoop, listScope }: Props) {
  const router = useRouter();
  const { cart } = useCartWithPendingStatus();
  const { eva, algoliaIndexId } = useConfiguration();
  const { serverSideProps } = useServerSideProps();

  const isCartPage = useMemo(() => {
    return router.pathname === appRoutes.CART;
  }, [router]);

  const productID = useMemo(() => {
    if (!isCartPage) {
      const productId = (router.query.slug as string)?.split("-").at(-1) ?? "";
      if (serverSideProps?.selected?.parent_product_ids?.includes(productId)) {
        return serverSideProps?.selected?.product_id ?? "";
      } else {
        return productId;
      }
    } else {
      if (productReferenceFields?.type === "trending-items") {
        return "";
      } else {
        const latestCarProduct = cartRecommenderElegibleProduct(cart);
        return latestCarProduct ? latestCarProduct : "";
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, isCartPage, productReferenceFields, router]);

  const carouselDataQuery = useMemo(() => {
    return [
      "algolia-product-carousel",
      productReferenceFields?.type ?? "strategy-not-defined",
      !!productID ? productID : "trending-items",
    ];
  }, [productID, productReferenceFields]);

  const { data } = useQuery({
    queryKey: carouselDataQuery,
    staleTime: Infinity,
    queryFn: async () => {
      if (productReferenceFields && productReferenceFields.type === "bought-together" && !productID) {
        return [];
      }

      const response = await recommenderRequest({
        algoliaIndexId,
        productReferenceFields,
        productID: productID || undefined,
      });
      if (response) {
        const productIDS = (response.hits as RecommendHit[]).map((item) => item.objectID);
        //Removed hook
        const productsInformation = await evaUtils.getProductsByIdsWithChildren(eva, productIDS);
        const mergedData = mergeHistWithProductsInformation(
          response.hits as RecommendHit[],
          productsInformation as any,
          response.queryID
        );
        return mergedData;
      } else {
        return [];
      }
    },
  });

  if (!data) {
    return <SkeletonCarousel entry={entry} />;
  }

  if (data.length === 0) {
    return null;
  }

  return (
    <CarouselWrapper entry={entry}>
      <ProductCarouselSwiper isAlgolia products={data} listScope={listScope} enableLoop={enableLoop} />
    </CarouselWrapper>
  );
}
